<template>
  <DefaultPanel :collapsed="true" @toggle="(v) => (isOpened = v)">
    <template #header>
      <div class="dataset-info is-size-6">
        <slot name="dataset-name" :dataset-name="name">
          <span class="mr-4">
            {{ name }}
          </span>
        </slot>
        <span class="is-size-7">
          <router-link
            :to="{
              name: 'userDetail',
              params: { userUsername: createdBy.username },
            }"
          >
            <span class="icon is-small is-size-7">
              <i class="fa-solid fa-user fa-xs" />
            </span>
            {{ createdBy.username }}
          </router-link>
          <span v-if="showModified">
            , last modified {{ modifStr }} by
            <router-link
              v-if="modifiedBy"
              :to="{
                name: 'userDetail',
                params: { userUsername: modifiedBy.username },
              }"
              @click.stop
            >
              {{ modifiedBy.username }}
            </router-link>
            <span v-else>Unknown</span>
            ,
          </span>
          <span v-else>, {{ creatStr }}</span>
        </span>
      </div>
    </template>

    <template #header-controls>
      <span class="is-flex mr-2">
        <button
          v-if="isOpened"
          v-tooltip="'Reload file content'"
          class="button is-small is-rounded mr-"
          @click.stop="loadDatasetContent"
        >
          <span class="icon">
            <i class="fa-solid fa-arrow-rotate-left"></i>
          </span>
        </button>
        <slot name="header-controls" />
      </span>
    </template>

    <div class="dataset-content" :class="{ 'is-loading': isLoading }">
      <div v-if="isLoading" class="loading-overlay">
        <span class="icon">
          <i class="fa-solid fa-spinner fa-spin"></i>
        </span>
      </div>
      <span
        v-if="hasError"
        class="notification is-fullwidth is-danger is-light"
      >
        {{ contentError.message }}
      </span>
      <template v-else>
        <textarea
          ref="textarea"
          :value="datasetContent ? `${datasetContent}\n` : ''"
          class="textarea"
          rows="5"
          readonly
          :placeholder="datasetContent === '' && '<empty>'"
          :disabled="isLoading"
        ></textarea>
        <span class="controls">
          <button
            v-tooltip="!isSupported && 'Copy to clipboard not supported'"
            class="button icon has-text-grey"
            :disabled="!isSupported"
            @click="copy"
          >
            <i class="fa-solid fa-copy"></i>
          </button>
          <button
            class="button is-ghost is-borderless icon has-text-grey"
            disabled
            style="opacity: 0.3"
          >
            <i class="fa-solid fa-lock fa-sm"></i>
          </button>
        </span>
      </template>
    </div>
    <p
      v-if="dataset?.id"
      class="dataset-links help is-size-7 has-text-right mx-2"
    >
      <router-link
        v-if="previewDatafileCopy"
        :to="{
          name: 'viewFile',
          query: {
            id: previewDatafileCopy.id,
            app: 'data_management',
            model: 'datafilecopies',
          },
        }"
        target="_blank"
        rel="noopener"
      >
        <span class="icon is-small is-size-7">
          <i class="fa-regular fa-eye fa-xs" />
        </span>
        Open preview
      </router-link>
      <router-link
        :to="{ path: `/data_management/datasets/default/${dataset.id}` }"
      >
        <span class="icon is-small is-size-7">
          <i class="fa-solid fa-arrow-up-right-from-square fa-xs" />
        </span>
        See dataset
      </router-link>
    </p>
  </DefaultPanel>
</template>
<script>
import { useClipboard } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'

import { get } from '@/api'
import { getDatasetPreviewDatafileCopy } from '@/api/files'
import { useStore } from '@/store'
import { getFileFlags } from '@/utils/fileFlags'

import DefaultPanel from '@/components/DefaultPanel.vue'

export default {
  components: {
    DefaultPanel,
  },
  props: {
    name: { type: String, required: true },
    created: { type: Object, required: true },
    modified: { type: Object, required: true },
    createdBy: { type: Object, required: true },
    modifiedBy: { type: Object, required: false },
    peekUrl: { type: String, required: true },
    dataset: { type: Object, default: null },
    dataType: { type: String, default: '' },
  },
  setup(props) {
    const store = useStore()
    const textarea = ref(false)
    const isOpened = ref(false)
    const isLoading = ref(false)
    const datasetContent = ref(null)
    const contentError = ref(null)
    const previewDatafileCopy = ref(null)
    const previewLoaded = ref(false)

    const modifStr = computed(() => props.modified && props.modified.fromNow())
    const creatStr = computed(() => props.created && props.created.fromNow())

    const showModified = computed(
      () =>
        props.modified &&
        props.created &&
        props.modifiedBy &&
        props.created.diff(props.modified, 'seconds') > 10,
    )

    async function loadDatasetContent() {
      datasetContent.value = null
      contentError.value = null
      isLoading.value = true
      try {
        const response = await get(props.peekUrl)
        datasetContent.value = response.data
        resizeTextarea()
      } catch {
        const errorMessage = `Error loading content of file ${
          props.dataset?.id || props.name
        }`
        contentError.value = new Error(errorMessage)
      } finally {
        isLoading.value = false
      }
    }

    const hasError = computed(() => contentError.value !== null)

    const notifyCopy = () =>
      store.commit('notify', {
        type: 'success',
        message: 'Copied file content to clipboard',
      })
    const { copy: copyToClipboard, isSupported } = useClipboard({
      source: datasetContent.value,
    })
    const copy = () => copyToClipboard(datasetContent.value) && notifyCopy()

    async function resizeTextarea() {
      await nextTick()
      if (datasetContent.value && textarea.value) {
        const textareaScrollHeight = textarea.value.scrollHeight + 5
        const height = Math.min(textareaScrollHeight, 400)
        textarea.value.style.height = `${height}px`
      }
    }

    async function loadPreview() {
      const datasetId = props.dataset?.id
      if (props.dataType !== 'REPORT' || !datasetId || previewLoaded.value)
        return

      try {
        const copy = await getDatasetPreviewDatafileCopy(datasetId)
        if (props.dataType !== 'REPORT' || datasetId !== props.dataset?.id)
          return

        previewDatafileCopy.value =
          copy && getFileFlags(copy).viewable ? copy : null
        previewLoaded.value = true
      } catch (error) {
        console.warn(
          `Could not resolve a preview file for Dataset ${datasetId}`,
          error,
        )
      }
    }

    watch(isOpened, (opened) => {
      if (!opened) return
      loadDatasetContent()
      loadPreview()
    })

    return {
      textarea,
      isLoading,
      isOpened,
      modifStr,
      creatStr,
      showModified,
      loadDatasetContent,
      datasetContent,
      contentError,
      hasError,
      previewDatafileCopy,
      // clipboard
      isSupported,
      copy,
    }
  },
}
</script>

<style lang="scss" scoped>
.dataset-info {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.dataset-content {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  margin: 0.5rem;

  textarea {
    min-height: 100px;
  }

  span.controls {
    position: absolute;
    top: 0;
    right: 0;
    margin: 0.5rem 0rem;
    padding: 0.5rem;
  }

  div.loading-overlay {
    position: absolute;
    display: flex;
    height: 100%;
    width: 100%;
    background-color: var(--white);
    padding: 2rem;
    opacity: 0.6;
    pointer-events: none;
    z-index: 1;
    & > .icon {
      margin: auto;
    }
  }
}

.dataset-links {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}
</style>
